import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import moment from 'moment';

import {
  GetMarathonCategoryContentPacksQuery,
  GetMarathonCategoryContentPacksQueryVariables,
  UpdateMarathonExcludedContentPacksMutation,
  UpdateMarathonExcludedContentPacksMutationVariables,
} from '../../../../../../__gqltypes__';
import Pagination from '../../../../curation/components/Pagination';
import { GET_MARATHON_CATEGORY_CONTENT_PACKS, UPDATE_MARATHON_EXCLUDED_CONTENT_PACKS } from '../graphql';

type Pack = GetMarathonCategoryContentPacksQuery['liveOps']['getMarathonCategoryContentPacks'][0];

type SortKey = 'popularity' | 'name' | 'songs' | 'availabilityDate';

const PAGE_SIZE = 50;

// Mirrors MARATHON_MIN_PLAYABLE_CONTENT_PACKS on the backend: a round picks 3 playlists per batch.
const MIN_PLAYABLE = 3;

type Props = {
  show: boolean;
  category: string;
  categoryName: string;
  // category id -> display name, to label the other tags of a playlist
  categoryNames: Record<string, string>;
  onHide: () => void;
  // called after a successful save with the new number of excluded playlists
  onSaved: (excludedCount: number) => void;
};

/*
 * Per-category exclusion list for marathon. The whole playlist list of the category is loaded once
 * (one Spanner scan either way) and searched, sorted and paged in the browser, so the admin can
 * tick the off-topic playlists (typically the ones also tagged with another decade) and save the
 * list in one go. Saving applies to every marathon event of the category.
 */
export function MarathonCategoryPacksModal({
  show,
  category,
  categoryName,
  categoryNames,
  onHide,
  onSaved,
}: Props): JSX.Element {
  const [loadPacks, { data, loading, error }] = useLazyQuery<
    GetMarathonCategoryContentPacksQuery,
    GetMarathonCategoryContentPacksQueryVariables
  >(GET_MARATHON_CATEGORY_CONTENT_PACKS, { fetchPolicy: 'network-only' });
  const [savePacks, { loading: saving, error: saveError }] = useMutation<
    UpdateMarathonExcludedContentPacksMutation,
    UpdateMarathonExcludedContentPacksMutationVariables
  >(UPDATE_MARATHON_EXCLUDED_CONTENT_PACKS);

  const [packs, setPacks] = useState<Pack[]>([]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [savedExcluded, setSavedExcluded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [onlyMultiTagged, setOnlyMultiTagged] = useState(false);
  const [onlyExcluded, setOnlyExcluded] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('popularity');
  const [sortDesc, setSortDesc] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (show && category) {
      setSearch('');
      setOnlyMultiTagged(false);
      setOnlyExcluded(false);
      setPage(1);
      loadPacks({ variables: { category } });
    }
  }, [show, category, loadPacks]);

  useEffect(() => {
    const list = data?.liveOps?.getMarathonCategoryContentPacks;
    if (list) {
      setPacks(list);
      const ids = new Set(list.filter((pack) => pack.excluded).map((pack) => pack.id));
      setExcluded(new Set(ids));
      setSavedExcluded(ids);
    }
  }, [data]);

  const otherCategories = useCallback((pack: Pack) => pack.categories.filter((tag) => tag !== category), [category]);

  const visiblePacks = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = packs.filter((pack) => {
      if (needle && !pack.name.toLowerCase().includes(needle) && !pack.id.toLowerCase().includes(needle)) {
        return false;
      }
      if (onlyMultiTagged && otherCategories(pack).length === 0) {
        return false;
      }
      if (onlyExcluded && !excluded.has(pack.id)) {
        return false;
      }
      return true;
    });
    const direction = sortDesc ? -1 : 1;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortKey === 'availabilityDate') {
        cmp = new Date(a.availabilityDate ?? 0).getTime() - new Date(b.availabilityDate ?? 0).getTime();
      } else {
        cmp = a[sortKey] - b[sortKey];
      }
      return cmp * direction;
    });
  }, [packs, search, onlyMultiTagged, onlyExcluded, excluded, sortKey, sortDesc, otherCategories]);

  const pageCount = Math.max(1, Math.ceil(visiblePacks.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagePacks = visiblePacks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const playable = packs.length - excluded.size;
  const tooFewLeft = packs.length > 0 && playable < MIN_PLAYABLE;

  const isDirty = useMemo(() => {
    if (excluded.size !== savedExcluded.size) {
      return true;
    }
    return Array.from(excluded).some((id) => !savedExcluded.has(id));
  }, [excluded, savedExcluded]);

  const toggle = (id: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleVisiblePage = (exclude: boolean) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      pagePacks.forEach((pack) => (exclude ? next.add(pack.id) : next.delete(pack.id)));
      return next;
    });
  };

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortDesc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortDesc(key !== 'name');
    }
    setPage(1);
  };

  const handleSave = async () => {
    const contentPackIds = Array.from(excluded);
    const result = await savePacks({ variables: { input: { category, contentPackIds } } }).catch(() => null);
    const list = result?.data?.updateMarathonExcludedContentPacks;
    if (list) {
      setPacks(list);
      const ids = new Set(list.filter((pack) => pack.excluded).map((pack) => pack.id));
      setExcluded(new Set(ids));
      setSavedExcluded(ids);
      onSaved(ids.size);
    }
  };

  const handleHide = () => {
    if (isDirty) {
      if (!window.confirm('Discard the unsaved changes to the excluded playlists?')) {
        return;
      }
      // Reopening re-fetches the list, but Apollo returns the same object when nothing changed and the
      // data effect above does not re-run, so the discarded ticks must be reset here.
      setExcluded(new Set(savedExcluded));
    }
    onHide();
  };

  const sortIcon = (key: SortKey) =>
    sortKey === key ? <i className={`fas fa-sort-${sortDesc ? 'down' : 'up'} ml-1`} /> : null;

  const excludedIcon = (pack: Pack) => (excluded.has(pack.id) ? 'text-muted' : '');

  return (
    <Modal show={show} onHide={handleHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          Marathon playlists - {categoryName}{' '}
          <Badge variant={excluded.size > 0 ? 'danger' : 'secondary'}>
            {excluded.size} excluded of {packs.length}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="small">
          A marathon of this category picks 3 random playlists per batch from every playlist tagged with it, so a
          playlist tagged with several decades or genres also plays here. Tick the playlists that should never be used
          for a <strong>{categoryName}</strong> marathon. This applies to every marathon event of this category,
          including a running one, within a few minutes.
        </Alert>
        <Row className="align-items-end mb-3">
          <Col md={4}>
            <Form.Label className="small mb-1">Search name or id</Form.Label>
            <Form.Control
              size="sm"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </Col>
          <Col xs="auto">
            <Form.Check
              type="switch"
              id="marathon-packs-multi-tagged"
              label="Only playlists with other categories"
              checked={onlyMultiTagged}
              onChange={() => {
                setOnlyMultiTagged((prev) => !prev);
                setPage(1);
              }}
            />
          </Col>
          <Col xs="auto">
            <Form.Check
              type="switch"
              id="marathon-packs-only-excluded"
              label="Only excluded"
              checked={onlyExcluded}
              onChange={() => {
                setOnlyExcluded((prev) => !prev);
                setPage(1);
              }}
            />
          </Col>
          <Col xs="auto" className="ml-auto">
            <Button size="sm" variant="outline-danger" className="mr-2" onClick={() => toggleVisiblePage(true)}>
              Exclude this page
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => toggleVisiblePage(false)}>
              Include this page
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error.message}</Alert>}
        {saveError && <Alert variant="danger">{saveError.message}</Alert>}
        {tooFewLeft && (
          <Alert variant="danger" className="small">
            Only {playable} playlist(s) would be left. A marathon needs at least {MIN_PLAYABLE} playable playlists in
            its category, so this list cannot be saved.
          </Alert>
        )}
        {loading ? (
          <Spinner animation="border" />
        ) : (
          <>
            <div className="small text-muted mb-2">
              {visiblePacks.length} playlists shown, sorted by {sortKey} {sortDesc ? 'descending' : 'ascending'}.
              Excluded rows are greyed out.
            </div>
            <Table size="sm" striped hover responsive>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Exclude</th>
                  <th role="button" onClick={() => sortBy('name')}>
                    Name{sortIcon('name')}
                  </th>
                  <th role="button" className="text-right" onClick={() => sortBy('songs')}>
                    Songs{sortIcon('songs')}
                  </th>
                  <th>Other categories</th>
                  <th role="button" className="text-right" onClick={() => sortBy('popularity')}>
                    Popularity{sortIcon('popularity')}
                  </th>
                  <th role="button" onClick={() => sortBy('availabilityDate')}>
                    Available since{sortIcon('availabilityDate')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagePacks.map((pack) => (
                  <tr key={pack.id} className={excludedIcon(pack)}>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        id={`marathon-pack-${pack.id}`}
                        checked={excluded.has(pack.id)}
                        onChange={() => toggle(pack.id)}
                      />
                    </td>
                    <td>
                      <a href={`/sp3/playlist/${pack.id}`} target="_blank" rel="noreferrer">
                        {pack.name || pack.id}
                      </a>
                    </td>
                    <td className="text-right">{pack.songs}</td>
                    <td>
                      {otherCategories(pack).length === 0 ? (
                        <span className="text-muted">-</span>
                      ) : (
                        otherCategories(pack).map((tag) => (
                          <Badge key={tag} variant="light" className="mr-1">
                            {categoryNames[tag] ?? tag}
                          </Badge>
                        ))
                      )}
                    </td>
                    <td className="text-right">{pack.popularity}</td>
                    <td className="small">
                      {pack.availabilityDate ? moment(pack.availabilityDate).format('YYYY-MM-DD') : '-'}
                    </td>
                  </tr>
                ))}
                {pagePacks.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-muted text-center">
                      No playlist matches the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <div className="d-flex justify-content-center">
              <Pagination active={currentPage} num={pageCount} onClick={setPage} />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isDirty && <span className="small text-warning mr-auto">Unsaved changes</span>}
        <Button variant="secondary" onClick={handleHide} disabled={saving}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || loading || !isDirty || tooFewLeft}>
          {saving ? 'Saving...' : `Save ${excluded.size} excluded`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
